import React, { useState, memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { useNavigate } from 'react-router-dom';

// [FEEDBACK] Limit to 8 items per grid
const ITEMS_PER_GRID = 8;

function ServiceGridNode({ data }) {
  // 1. props로 전달받은 onShowDetails 함수를 data 객체에서 추출합니다.
  const { theme, onClose, onShowDetails, totalCount } = data;
  const navigate = useNavigate();

  const services = data.services || [];
  // Display only the first 8 items
  // Pagination Logic
  const [currentPage, setCurrentPage] = useState(0);
  const totalPages = Math.ceil(services.length / ITEMS_PER_GRID);

  const startIndex = currentPage * ITEMS_PER_GRID;
  const displayServices = services.slice(startIndex, startIndex + ITEMS_PER_GRID);

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentPage(prev => Math.max(0, prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
  };

  const handleServiceClick = (event, service) => {
    event.stopPropagation();
    if (onShowDetails) {
      onShowDetails(service);
    }
  };

  return (
    <>
      {/* 부모(G2) 노드와 연결될 핸들 */}
      <Handle type="target" position={data.targetPosition || Position.Right} className="w-4 h-4 !bg-orange-500" />

      <div
        className={`bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border-2 ${theme === 'light' ? 'border-gray-200' : 'border-gray-600'} w-[340px] overflow-hidden`}
        onClick={(event) => event.stopPropagation()}
      >
        {/* 헤더 */}
        <div className={`flex justify-between items-center p-3 border-b ${theme === 'light' ? 'bg-gray-100 border-gray-200' : 'bg-gray-700 border-gray-600'}`}>
          <h3 className={`text-lg font-bold ${theme === 'light' ? 'text-gray-800' : 'text-gray-100'}`}>
            관련 서비스
            <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">({totalCount || services.length})</span>
          </h3>

          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 2x4 Grid for 8 items */}
        <div className="grid grid-cols-4 gap-2 p-3 bg-gray-50 dark:bg-gray-800/50 min-h-[180px]">
          {displayServices.map(service => (
            <div
              key={service.id}
              onClick={(e) => handleServiceClick(e, service)}
              className="group flex flex-col items-center p-2 bg-white dark:bg-gray-700 rounded-xl hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:shadow-md transition-all duration-200 cursor-pointer text-center border border-transparent hover:border-blue-200 dark:hover:border-blue-700"
            >
              <div className="w-10 h-10 mb-1 flex items-center justify-center bg-white dark:bg-gray-600 rounded-lg p-1 shadow-sm">
                <img
                  src={service.imageUrl || 'https://placehold.co/64x64/e2e8f0/e2e8f0?text=..'}
                  alt={`${service.label} 로고`}
                  className="w-full h-full object-contain"
                  onError={e => { e.target.onerror = null; e.target.src = '/Images/Logos/Base.jpg'; }}
                />
              </div>
              <span className={`text-[10px] font-medium leading-tight line-clamp-2 w-full ${theme === 'light' ? 'text-gray-600' : 'text-gray-300'} group-hover:text-blue-700 dark:group-hover:text-blue-300`}>
                {service.label}
              </span>
            </div>
          ))}
        </div>

        {/* Pagination Footer */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center p-2 bg-gray-100 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
            <button
              onClick={handlePrev}
              disabled={currentPage === 0}
              className={`p-1 rounded-full transition-colors ${currentPage === 0 ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>

            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {currentPage + 1} / {totalPages}
            </span>

            <button
              onClick={handleNext}
              disabled={currentPage === totalPages - 1}
              className={`p-1 rounded-full transition-colors ${currentPage === totalPages - 1 ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default memo(ServiceGridNode);
